import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { requiredIf } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";

import { IPromptOptions } from "../../../../../resources-vue/vue-dialogs/prompt/prompt";
import { TaskService } from "../../../../../services/task.service";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import { ToastrService } from "../../../../../services/toastr.service";
import { PatientService } from "../../../../../services/patient.service";
import {
    ApplicationEnum,
    ConfirmationStatus,
    ConfirmationStatusEnum,
    ParentPermissionEnum,
    PermissionActionEnum,
    VisitStatusEnum
} from "../../../../../enums/enums";
import { enumToMap } from "../../../../../common/vue-helpers/modifiers/enum-modifiers";
import {
    formatDateInput,
    sanitizeMarkup,
    formatDate
} from "../../../../../common/vue-helpers/modifiers/value-modifier";

import FormSection from "../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import DatePicker from "../../../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";
import TimePicker from "../../../../../resources-vue/vue-custom-elements/TimePicker/TimePicker.vue";
import TypeaheadInput from "../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import Prompt from "../../../../../resources-vue/vue-dialogs/prompt/prompt.vue";
import CommentInput from "../../../../../resources-vue/vue-custom-elements/CommentInput/CommentInput.vue";
import VisitDocumentsHosPal from "../VisitDocumentsHosPal/VisitDocumentsHosPal.vue";

export default defineComponent({
    components: {
        FormSection,
        DatePicker,
        TimePicker,
        CommentInput,
        VisitDocumentsHosPal,
        TypeaheadInput
    },
    inject: ["dialogRef"],
    props: {
        visitDetails: {
            type: Object
        },
        cancel: {
            type: Function
        },
        isSaving: {
            type: Boolean,
            default: false
        },
        ok: {
            type: Function
        },
        isPlanner: {
            type: Boolean,
            default: false
        }
    },
    data(vm) {
        return {
            _taskService: null,
            _toastrService: null,
            _patientService: null,
            _permissionManager: null as PermissionManager,
            _dialogRef: this.dialogRef as any,
            taskDetails: null,
            disableScheduleDate: false,
            disableVisitEndTime: true,
            assignedToName: "",
            isPalliative: true,
            payers: null,
            confirmationStatusEnum: ConfirmationStatus,
            confirmationStatus: 0,
            isLoading: false as boolean,
            surchargeTooltip: "Can be used in instances such as: parking, tolls, etc.",
            assignedToMissedVisitFormReturnReason: "",
            assignedToComments: "",
            assignedToReturnReason: "",
            assignedToTaskNotes: "",
            tasks: null,
            formatDateInput,
            sanitizeMarkup,
            enumToMap,
            v$: useVuelidate(),
            formatDate,
            disable: false,
            canViewTaskNotesHos: false as boolean,
            canViewTaskNotesPal: false as boolean
        };
    },
    validations() {
        return {
            taskDetails: {
                scheduleEvent: {
                    nvaStartDate: { required: requiredIf(this.visitDetails.IsNonVisitAssignment) },
                    eventDate: { required: requiredIf(!this.visitDetails.IsNonVisitAssignment) },
                    timeIn: { required: requiredIf(this.isVisitTimeRequired && !this.isPlanner) },
                    timeOut: { required: requiredIf(this.isVisitTimeRequired && !this.isPlanner) }
                }
            }
        };
    },

    emits: ["update:isSaving"],

    async created() {
        this._taskService = Container.instance.get(TaskService);
        this._toastrService = Container.instance.get(ToastrService);
        this._patientService = Container.instance.get(PatientService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this.canViewTaskNotesHos = this._permissionManager.checkPermissionByProduct(
            ParentPermissionEnum.taskNotes,
            PermissionActionEnum.canView,
            ApplicationEnum.AxxessHospiceFE
        );
        this.canViewTaskNotesPal = this._permissionManager.checkPermissionByProduct(
            ParentPermissionEnum.taskNotes,
            PermissionActionEnum.canView,
            ApplicationEnum.AxxessPalliative
        );
        this.getPayers();
        if (
            (this.visitDetails.Status == "Not Yet Due" ||
                this.visitDetails.Status == "Not Yet Started" ||
                this.visitDetails.StatusName == "Not Yet Due" ||
                this.visitDetails.StatusName == "Not Yet Started") &&
            !this.visitDetails.IsNonVisitAssignment
        ) {
            this.disable = true;
        }
        this.disableScheduleDate = this.visitDetails.Status == "Completed";
        this.isPalliative = this.visitDetails.Application == ApplicationEnum.AxxessPalliative;
        try {
            this.isLoading = true;
            this.taskDetails = await this._taskService.getTasksDetails({
                agencyId: this.visitDetails.AgencyId,
                application: this.visitDetails.Application,
                eventId: this.visitDetails.Id,
                patientId: this.visitDetails.PatientId
            });
            this.assignedToName =
                this.taskDetails.scheduleEvent.userFirstName + " " + this.taskDetails.scheduleEvent.userLastName;
            if (this.taskDetails.scheduleEvent.timeIn) {
                this.disableVisitEndTime = false;
            }
            if (this.isPalliative) {
                if (this.taskDetails.scheduleEvent.isVisitVerified)
                    this.confirmationStatus = ConfirmationStatusEnum.VisitConfirmed;
                if (this.taskDetails.scheduleEvent.patientNotified)
                    this.confirmationStatus = ConfirmationStatusEnum.ResceduleVisit;
            } else {
                this.confirmationStatus = Number(this.taskDetails.scheduleEvent.confirmationStatus);
            }

            this.tasks = await this._taskService.getTasksByPatients({
                start: this.taskDetails.scheduleEvent.eventDate,
                end: this.taskDetails.scheduleEvent.eventDate,
                patientIds: [this.visitDetails.PatientId]
            });
            this.comparedAndExtractValues();
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
        }
    },

    watch: {
        confirmationStatus(newValue) {
            if (newValue == ConfirmationStatusEnum.VisitConfirmed) {
                this.taskDetails.scheduleEvent.isVisitVerified = true;
                this.taskDetails.scheduleEvent.patientNotified = false;
                this.taskDetails.scheduleEvent.confirmationStatus = ConfirmationStatusEnum.VisitConfirmed;
            } else if (newValue == ConfirmationStatusEnum.ResceduleVisit) {
                this.taskDetails.scheduleEvent.patientNotified = true;
                this.taskDetails.scheduleEvent.isVisitVerified = false;
                this.taskDetails.scheduleEvent.confirmationStatus = ConfirmationStatusEnum.ResceduleVisit;
            } else {
                this.taskDetails.scheduleEvent.patientNotified = false;
                this.taskDetails.scheduleEvent.isVisitVerified = false;
                this.taskDetails.scheduleEvent.confirmationStatus = 0;
            }
        }
    },
    computed: {
        areFieldsEnabled() {
            if (this.isPlanner) {
                return (
                    this.visitDetails.StatusName !== VisitStatusEnum.NotYetDue &&
                    this.visitDetails.StatusName !== VisitStatusEnum.NotStarted
                );
            } else {
                return (
                    this.visitDetails.Status === VisitStatusEnum.Completed ||
                    this.visitDetails.Status === VisitStatusEnum.SubmittedCoSignature ||
                    this.visitDetails.Status === VisitStatusEnum.SubmittedWithSignature ||
                    this.visitDetails.Status === VisitStatusEnum.SubmittedWithSignaturePal ||
                    this.visitDetails.Status === VisitStatusEnum.ExportReady ||
                    this.visitDetails.Status === VisitStatusEnum.Exported ||
                    this.visitDetails.Status === VisitStatusEnum.MissedVisit ||
                    this.visitDetails.Status === VisitStatusEnum.NotExported
                );
            }
        },
        isSavedOrCompleted() {
            return (
                this.visitDetails.Status === VisitStatusEnum.NotYetDue ||
                this.visitDetails.Status === VisitStatusEnum.NotStarted
            );
        },
        isConfirmationStatusEnabled() {
            if (this.isPlanner) {
                return (
                    this.visitDetails.StatusName !== VisitStatusEnum.NotYetDue &&
                    this.visitDetails.StatusName !== VisitStatusEnum.NotStarted
                );
            } else {
                return (
                    this.visitDetails.Status === VisitStatusEnum.Saved ||
                    this.visitDetails.Status === VisitStatusEnum.Returned ||
                    this.visitDetails.Status === VisitStatusEnum.Reopened ||
                    this.visitDetails.Status === VisitStatusEnum.PendingAddendum
                );
            }
        },
        isVisitTimeRequired() {
            return (
                this.visitDetails.Status === VisitStatusEnum.Completed ||
                this.visitDetails.Status === VisitStatusEnum.SubmittedCoSignature ||
                this.visitDetails.Status === VisitStatusEnum.SubmittedWithSignature ||
                this.visitDetails.Status === VisitStatusEnum.SubmittedWithSignaturePal ||
                this.visitDetails.Status === VisitStatusEnum.ExportReady ||
                this.visitDetails.Status === VisitStatusEnum.Exported ||
                this.visitDetails.Status === VisitStatusEnum.MissedVisit ||
                this.visitDetails.Status === VisitStatusEnum.NotExported
            );
        },

        showComments() {
            return this.isPalliative
                ? !this.visitDetails.IsNonVisitAssignment && this.canViewTaskNotesPal
                : !this.visitDetails.IsNonVisitAssignment && this.canViewTaskNotesHos;
        }
    },
    methods: {
        async getPayers() {
            this.payers = await this._patientService.getPayersByPatientId({
                patientId: this.visitDetails.PatientId,
                applications: this.visitDetails.Application,
                taskId: this.visitDetails.Id,
                accountId: this.visitDetails.AgencyId
            });
        },
        updateVisitType(isCallVisit: boolean, isPRNVisit: boolean) {
            this.taskDetails.scheduleEvent.isCallVisit = isCallVisit;
            this.taskDetails.scheduleEvent.isPRNVisit = isPRNVisit;
        },
        async saveVisit() {
            const validated = await this.v$.$validate();
            if (!!this.taskDetails && validated) {
                try {
                    this.$emit("update:isSaving", true);
                    this.taskDetails.scheduleEvent.application = this.visitDetails.Application;
                    this.taskDetails.scheduleEvent.isNonVisit = this.visitDetails.IsNonVisitAssignment;
                    await this._taskService.updateTasksDetails(this.taskDetails.scheduleEvent);
                    this._toastrService.success({
                        title: this.visitDetails.IsNonVisitAssignment ? "Non-Visit Task Updated" : "Visit Updated",
                        message: this.visitDetails.IsNonVisitAssignment
                            ? "The Task has been successfully Updated"
                            : "Visit has been successfully Updated"
                    });
                    this.ok();
                } catch (e) {
                    this._toastrService.error({
                        title: this.visitDetails.IsNonVisitAssignment
                            ? "Non-Visit Task Update Failed"
                            : "Visit Update Failed",
                        message: this.visitDetails.IsNonVisitAssignment
                            ? "Updating Task Operation Failed, Please Contact Axxess if issue still persists."
                            : "Updating Visit Operation Failed, Please Contact Axxess if issue still persists."
                    });
                } finally {
                    this.$emit("update:isSaving", false);
                }
            } else {
                this._toastrService.error({
                    title: `Validation Error`,
                    message: "Please fill required fields"
                });
            }
        },
        handleDateChange(date: any, name: string) {
            if (name === "eventDate") this.taskDetails.scheduleEvent.eventDate = date;
            if (name === "nvaStartDate") this.taskDetails.scheduleEvent.nvaStartDate = date;
        },
        changetimeIn(time: any) {
            this.taskDetails.scheduleEvent.timeIn = time;
            this.taskDetails.scheduleEvent.timeIn
                ? (this.disableVisitEndTime = false)
                : (this.disableVisitEndTime = true);
        },
        changetimeOut(time: any) {
            this.taskDetails.scheduleEvent.timeOut = time;
        },
        changeTravelTimeIn(time: any) {
            this.taskDetails.scheduleEvent.travelTimeIn = time;
        },
        changeTravelTimeOut(time: any) {
            this.taskDetails.scheduleEvent.travelTimeOut = time;
        },
        closeDialog() {
            const promptOptions: IPromptOptions = {
                message: "Are you sure you want to leave the page? Please make sure you saved your work.",
                okText: "Yes, I'm Sure",
                cancelText: "Never Mind"
            };

            this._dialogRef = this.$dialog.open(Prompt, {
                props: {
                    modal: true,
                    contentStyle: {
                        overflowX: "hidden"
                    },
                    showHeader: false
                },
                data: {
                    promptOptions
                },
                onClose: (options) => {
                    if (options.data === "success") {
                        this.cancel();
                    }
                }
            });
        },

        resultUpdatedAppended(value: string) {
            this.taskDetails.scheduleEvent.returnReason += value;
        },

        resultUpdated(value: string) {
            this.taskDetails.scheduleEvent.returnReason = value;
        },

        resultUpdatedNarrativeAppended(value: string) {
            this.taskDetails.scheduleEvent.comments += value;
        },

        resultUpdatedNarrative(value: string) {
            this.taskDetails.scheduleEvent.comments = value;
        },

        async comparedAndExtractValues() {
            if (!this.taskDetails || !this.tasks || !Array.isArray(this.tasks)) {
                return;
            }

            const eventId = this.taskDetails.eventId || this.taskDetails.scheduleEvent?.eventId;
            const userId = this.taskDetails.eventId || this.taskDetails.scheduleEvent?.userId;
            const agencyId = this.taskDetails.agencyId || this.taskDetails.scheduleEvent?.agencyId;
            const patientId = this.taskDetails.patientId || this.taskDetails.scheduleEvent?.patientId;

            const matchingTask = this.tasks.find((task) => {
                const taskId = task.Id;
                const taskUserId = task.UserId;
                const taskAgencyId = task.AgencyId;
                const taskPatientId = task.PatientId;
                return (
                    taskId === eventId &&
                    taskUserId === userId &&
                    taskAgencyId === agencyId &&
                    taskPatientId === patientId
                );
            });

            if (matchingTask) {
                if (matchingTask.Comments && Array.isArray(matchingTask.Comments)) {
                    this.extractFromCommentsArray(matchingTask.Comments);
                }
            } else {
                this.assignedToMissedVisitFormReturnReason = "";
                this.assignedToComments = "";
                this.assignedToReturnReason = "";
                this.assignedToTaskNotes = "";
            }
        },

        extractFromCommentsArray(Comments: any) {
            if (!Comments || !Array.isArray(Comments)) {
                return;
            }

            const normalised = Comments.map((variableType) => {
                if (variableType && typeof variableType === "object") {
                    return {
                        Comment: variableType.Comment ?? variableType.comment ?? "",
                        CommentType: variableType.CommentType ?? null,
                        CommentColor: variableType.CommentColor ?? "",
                        MultiComments:
                            variableType.MultiComments ??
                            variableType.multicomments ??
                            variableType.multiComments ??
                            null
                    };
                }
                if (variableType && typeof variableType === "string") {
                    return {
                        Comment: variableType,
                        MultiComments: null
                    };
                }
                return {
                    Comment: "",
                    MultiComments: null,
                    CommentType: null,
                    CommentColor: null
                };
            });

            const missedVisitReasonEntry = normalised.find(
                (x) => typeof x.Comment === "string" && x.Comment.toLowerCase().includes("missed visit reason")
            );
            const taskNotes = normalised.find((x) => x.CommentType === 1 && x.CommentColor === "Yellow");

            if (missedVisitReasonEntry && typeof missedVisitReasonEntry.Comment === "string") {
                const text = missedVisitReasonEntry.Comment;
                const reasonMatch = text.match(/missed\s+visit\s+reason\s*:\s*([^\n]+?)(?:\n|$|comments\s*:)/i);
                if (reasonMatch && reasonMatch[1]) {
                    this.assignedToMissedVisitFormReturnReason = reasonMatch[1].trim();
                }
                let commentsMatch = text.match(/comments\s*:\s*(.+)$/i);
                if (!commentsMatch) {
                    commentsMatch = text.match(/\n(.+)$/);
                }
                if (commentsMatch && commentsMatch[1]) {
                    this.assignedToComments = commentsMatch[1].trim();
                }

                if (missedVisitReasonEntry.MultiComments) {
                    this.assignedToReturnReason = Array.isArray(missedVisitReasonEntry?.MultiComments)
                        ? missedVisitReasonEntry?.MultiComments.join(" | ")
                        : missedVisitReasonEntry?.MultiComments;
                }
            }

            if (!this.assignedToReturnReason || !this.assignedToTaskNotes) {
                const multiCommentOnly = normalised.find(
                    (x) => x.MultiComments && (!x.Comment || x.Comment.trim() === "")
                );
                if (taskNotes) {
                    const multi = multiCommentOnly?.MultiComments;
                    if (Array.isArray(multi)) {
                        const formatted = multi
                            .map((item) =>
                                item
                                    .split("\n")
                                    .map((x: string) => x.trim())
                                    .join("\n")
                            )
                            .join("\n\n");
                        this.assignedToTaskNotes = formatted;
                    } else {
                        this.assignedToTaskNotes = multi;
                    }
                } else {
                    this.assignedToReturnReason = Array.isArray(multiCommentOnly?.MultiComments)
                        ? multiCommentOnly?.MultiComments.join(" | ")
                        : multiCommentOnly?.MultiComments;
                }
            }
        }
    }
});
